import { cn } from "@u/cn";
import { type LucideIcon } from "lucide-react";
import { type ReactNode } from "react";

export type DashboardTimelineTone =
  | "neutral"
  | "brand"
  | "success"
  | "warning"
  | "danger";

const DOT_TONE: Record<DashboardTimelineTone, string> = {
  neutral: "bg-muted-foreground/40 text-muted-foreground",
  brand: "bg-primary/15 text-primary",
  success: "bg-success/15 text-success-strong",
  warning: "bg-warning/15 text-warning-strong",
  danger: "bg-destructive/15 text-destructive-strong",
};

export interface DashboardTimelineItem {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  /** Small muted line under the title — e.g. the author who logged it. */
  byline?: ReactNode;
  timestamp?: ReactNode;
  icon?: LucideIcon;
  tone?: DashboardTimelineTone;
}

export interface DashboardTimelineProps {
  items: DashboardTimelineItem[];
  className?: string;
}

/** Vertical activity / history / audit-log timeline. */
export function DashboardTimeline({ items, className }: DashboardTimelineProps) {
  return (
    <ol className={cn("relative space-y-6", className)}>
      {items.map((item, i) => {
        const tone = item.tone ?? "neutral";
        const Icon = item.icon;
        const isLast = i === items.length - 1;
        return (
          <li key={item.id} className="relative flex gap-4">
            {!isLast && (
              <span
                aria-hidden
                className="absolute left-3.5 top-8 bottom-[-1.5rem] w-px bg-border/60"
              />
            )}
            <span
              className={cn(
                "relative z-10 shrink-0 flex h-7 w-7 items-center justify-center rounded-full",
                DOT_TONE[tone],
              )}
            >
              {Icon ? <Icon size={13} /> : <span className="h-2 w-2 rounded-full bg-current" />}
            </span>
            <div className="min-w-0 flex-1 -mt-0.5">
              <div className="flex items-baseline justify-between gap-2">
                <p className="min-w-0 break-words text-sm text-foreground">
                  {item.title}
                </p>
                {item.timestamp && (
                  <span className="shrink-0 label-nano text-muted-foreground">
                    {item.timestamp}
                  </span>
                )}
              </div>
              {item.description && (
                <p className="text-xs text-muted-foreground mt-0.5 break-words">
                  {item.description}
                </p>
              )}
              {item.byline && (
                <p className="label-nano text-muted-foreground/80 mt-0.5">
                  {item.byline}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
